import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function Signup() {
  const { signup } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await signup(form);
      toast.success("Account created. Check your inbox to verify your email.");
      navigate("/account");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid place-items-center px-6 py-16" data-testid="signup-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center">
          <Logo variant="monogram" className="h-14 w-14 dark:invert" loading="eager" />
        </div>
        <h1 className="mt-10 text-4xl font-serif tracking-tighter text-center">Create Account</h1>
        <p className="mt-3 text-sm opacity-60 text-center">Save your favorites, track orders, unlock offers.</p>

        <button
          type="button"
          onClick={google}
          className="mt-10 w-full h-12 border border-foreground/30 hover:border-foreground flex items-center justify-center gap-3 label-xs"
          data-testid="signup-google-btn"
          style={{ transitionProperty: "border-color" }}
        >
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-4">
          <div className="flex-1 border-t" />
          <span className="label-xs opacity-50">OR</span>
          <div className="flex-1 border-t" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="label-xs">Full name</Label>
            <Input
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="rounded-none mt-2"
              data-testid="signup-name"
            />
          </div>
          <div>
            <Label className="label-xs">Email</Label>
            <Input
              required
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className="rounded-none mt-2"
              data-testid="signup-email"
            />
          </div>
          <div>
            <Label className="label-xs">Phone (optional)</Label>
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className="rounded-none mt-2"
              data-testid="signup-phone"
            />
          </div>
          <div>
            <Label className="label-xs">Password</Label>
            <Input
              required
              minLength={8}
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              className="rounded-none mt-2"
              data-testid="signup-password"
            />
            <p className="mt-1 text-[11px] opacity-60">Minimum 8 characters.</p>
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-none label-xs"
            data-testid="signup-submit"
          >
            {busy ? "Creating…" : "Create Account"}
          </Button>
        </form>

        <p className="mt-8 text-sm text-center opacity-80">
          Already have an account?{" "}
          <Link to="/login" className="link-underline" data-testid="signup-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
